<template>
  <button
    class="btn ms-button ms-button-style"
    :class="[buttonStyle, buttonStyleSize, buttonFont, buttonCustomStyle]"
    :disabled="disabled"
    :type="buttonAttribute"
    @click="handleClick"
  >
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed, PropType, ButtonHTMLAttributes } from 'vue'
import '@/styles/ms-button-styles.scss'
import { ButtonSize, ButtonStyles } from '@/utilities/enums'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  buttonType: {
    type: String as PropType<ButtonStyles>,
    required: false
  },
  buttonCustomStyle: {
    type: String,
    required: false
  },
  buttonSize: {
    type: String as PropType<ButtonSize>,
    required: false
  },
  buttonFont: {
    type: String,
    default: 'NKT-P1-medium'
  },
  buttonAttribute: {
    type: String as PropType<ButtonHTMLAttributes['type']>,
    default: 'button'
  },
  width: {
    type: String,
    required: false,
    default: '$ms-button-standard-height'
  },
  height: {
    type: String,
    required: false,
    default: '$ms-button-standard-min-width'
  },
  padding: {
    type: String,
    required: false,
    default: '$ms-button-standard-padding'
  }
})

const emit = defineEmits(['click'])

const handleClick = () => {
  emit('click')
}

const buttonStyle = computed(() => {
  switch (props.buttonType) {
    case ButtonStyles.PrimaryBlue:
      return 'ms-button-primary-blue'
    case ButtonStyles.PrimaryOrange:
      return 'ms-button-primary-orange'
    case ButtonStyles.PrimaryGreen:
      return 'ms-button-primary-green'
    case ButtonStyles.Secondary:
      return 'ms-button-secondary'
    case ButtonStyles.Tertiary:
      return 'ms-button-tertiary'
    default:
      return 'ms-button-none'
  }
})

const buttonStyleSize = computed(() => {
  switch (props.buttonSize) {
    case ButtonSize.Standard:
      return 'ms-button-standard-size'
    case ButtonSize.Details:
      return 'ms-button-details-size'
    case ButtonSize.Small:
      return 'ms-button-small-size'
    case ButtonSize.Custom:
      return 'ms-button-custom-size'
    case ButtonSize.NoPadding:
      return 'ms-button-none-size'
    default:
      return ''
  }
})
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.ms-button-custom-size {
  height: v-bind('height');
  min-width: v-bind('width');
  padding: v-bind('padding');
}
</style>
